import { useMemo } from "react";

import { ScaleLinear } from "@mui/x-charts-vendor/d3-scale";
import { area, curveBumpX } from "@mui/x-charts-vendor/d3-shape";
import { useYScale, useXAxis, useLineSeries } from "@mui/x-charts/hooks";
import { isDefined } from "@bridgesplit/utils";

import { useAppPalette } from "../../theme";
import { SeriesConfig, DeltaSeriesConfig } from "./types";
import { useProjectionDefIds } from "./ChartsProjection";

export default function DeltaAreaPlot<T extends { date: number }>({ series }: { series: SeriesConfig<T>[] }) {
    const deltaFillSeries = useMemo(() => series.filter((s) => s.deltaFill), [series]);

    if (deltaFillSeries.length === 0) return null;

    return (
        <>
            {deltaFillSeries.map((s) => (
                <DeltaArea key={s.dataKey} id={`${s.dataKey}-delta`} {...(s.deltaFill as DeltaSeriesConfig<T>)} />
            ))}
        </>
    );
}

function DeltaArea<T extends { date: number }>({
    valueSeries,
    referenceSeries,
    valueFillColor,
    referenceFillColor,
    id
}: DeltaSeriesConfig<T> & { id: string }) {
    const { success, error } = useAppPalette();
    const seriesData = useLineSeries();

    const valueSeriesData = seriesData.find((s) => s.dataKey === valueSeries)?.data ?? [];
    const referenceSeriesData = seriesData.find((s) => s.dataKey === referenceSeries)?.data ?? [];

    return (
        <>
            <DeltaFill
                primarySeriesData={valueSeriesData}
                secondarySeriesData={referenceSeriesData}
                color={valueFillColor ?? success}
                id={`${id}-value`}
            />
            {referenceFillColor !== undefined && (
                <DeltaFill
                    primarySeriesData={referenceSeriesData}
                    secondarySeriesData={valueSeriesData}
                    color={referenceFillColor ?? error}
                    id={`${id}-ref`}
                />
            )}
        </>
    );
}

/** Clips a filled rect to the region where primarySeriesData >= secondarySeriesData */
function DeltaFill({
    primarySeriesData,
    secondarySeriesData,
    color,
    opacity = 0.2,
    id
}: {
    primarySeriesData: readonly (number | null)[];
    secondarySeriesData: readonly (number | null)[];
    color: string;
    opacity?: number;
    id: string;
}) {
    const yScale = useYScale() as ScaleLinear<number, number>;
    const { data: xData = [], scale: xScale } = useXAxis();
    const { historyClipId, projectionClipId, dotMaskId } = useProjectionDefIds();

    const clipPath = useMemo(() => {
        type Point = { i: number; p: number; s: number };
        const points: Point[] = [];
        for (let i = 0; i < primarySeriesData.length; i++) {
            const p = primarySeriesData[i];
            const s = secondarySeriesData[i];
            if (!isDefined(p) || !isDefined(s) || !isDefined(xData[i])) continue;
            points.push({ i, p, s });
        }

        // Match line series' monotoneX curve so the fill edge tracks the rendered line
        const generator = area<Point>()
            .x((d) => xScale(xData[d.i]) ?? 0)
            .y0((d) => yScale(d.s))
            .y1((d) => yScale(Math.max(d.p, d.s)))
            .curve(curveBumpX);

        return generator(points) ?? "";
    }, [primarySeriesData, secondarySeriesData, xData, xScale, yScale]);

    return (
        <>
            <defs>
                <clipPath id={id}>
                    <path d={clipPath} />
                </clipPath>
            </defs>
            <g clipPath={`url(#${id})`}>
                <g clipPath={`url(#${historyClipId})`}>
                    <rect x={0} y={0} width="100%" height="100%" fill={color} opacity={opacity} />
                </g>
                <g clipPath={`url(#${projectionClipId})`} mask={`url(#${dotMaskId})`}>
                    <rect x={0} y={0} width="100%" height="100%" fill={color} opacity={opacity} />
                </g>
            </g>
        </>
    );
}
